import { useState, type ReactNode } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  testId?: string;
  /** When true, the header (title + chevron) stays fixed and only the
   * content area scrolls internally, instead of the whole card growing
   * with its content (e.g. Equipment Pricing's long item list). */
  scrollableContent?: boolean;
  ariaLabel?: string;
}

/**
 * Bordered card with a title row and expand/collapse chevron — the repeated
 * shell used across every read-only view-plan tab (Plan Details, each
 * Pricing & Billing subsection, each service row, Equipment Pricing,
 * Discount Logic).
 */
const CollapsibleSection = ({
  title,
  children,
  defaultExpanded = true,
  testId,
  scrollableContent = false,
  ariaLabel,
}: CollapsibleSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div
      className={`flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4 ${scrollableContent && isExpanded ? 'flex-1 min-h-0' : ''}`}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex shrink-0 items-center justify-between w-full cursor-pointer"
        aria-expanded={isExpanded}
      >
        <span className="text-[18px] font-medium leading-[26px] text-on-surface-variant">
          {title}
        </span>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-on-surface-variant shrink-0" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-on-surface-variant shrink-0" />
        )}
      </button>
      {isExpanded && (
        <div
          className={
            scrollableContent
              ? 'flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto'
              : 'flex flex-col gap-8'
          }
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;
